/**
 * workflowNodeMeta - Metadados visuais e padrões de configuração dos nodes v2
 *
 * Centraliza rótulos, ícones e o `config` inicial de cada bloco, de forma que a
 * paleta, o canvas BPMN e o inspetor falem exatamente o mesmo contrato lido
 * pelo motor (`workflow_enter_node`).
 *
 * @version 3.1.0
 * RELEASE: 2026-09-04 v3.1.0 - Resumo do cartão indica a regra de liberação
 *          (`config.event_match`): "todos" quando o bloco exige que todos os
 *          acontecimentos ocorram; sem a chave, segue mostrando o padrão atual.
 * @version 3.0.0
 * RELEASE: 2026-09-04 v3.0.0 - Paleta consolidada: "Decisão" (absorve a decisão
 *          exclusiva) e "Execução paralela" (junção virou um modo dela).
 * @version 2.1.0
 * RELEASE: 2026-08-31 v2.1.0 - Novo bloco "Exigência (pré-requisito)".
 * @version 2.0.0
 * RELEASE: 2026-08-31 v2.0.0 - Ícones distintos por símbolo BPMN e novo bloco
 *          "Evento intermediário" (o evento sai da linha e passa para o bloco).
 * RELEASE: 2026-08-30 v1.0.0 - Criação inicial (designer v2)
 */

import {
  Play,
  Zap,
  Filter,
  Split,
  Timer,
  MailQuestion,
  ShieldAlert,
  UserCheck,
  Workflow as WorkflowIcon,
  Flag,
  type LucideIcon,
} from 'lucide-react';

import type { WorkflowNodeType } from '@/hooks/useWorkflowDesigner';

/** Itens da paleta: alguns tipos aparecem em mais de uma variação (gateway/espera) */
export interface WorkflowPaletteItem {
  key: string;
  nodeType: WorkflowNodeType;
  label: string;
  description: string;
  icon: LucideIcon;
  /** Grupo exibido na paleta */
  grupo: 'Atividades' | 'Decisões' | 'Eventos' | 'Encerramento';
  /** Configuração inicial gravada em `workflow_nodes.config` */
  defaultConfig: Record<string, unknown>;
}

// Observação: o bloco "Início" não aparece na paleta — todo processo já nasce
// com ele criado automaticamente (igual ao módulo de Fluxos).
export const WORKFLOW_PALETTE: WorkflowPaletteItem[] = [
  {
    key: 'acao',
    nodeType: 'acao',
    label: 'Tarefa automática',
    description: 'Executa ações do catálogo (mover fase, definir vendedor, criar tarefa...).',
    icon: Zap,
    grupo: 'Atividades',
    defaultConfig: {},
  },
  {
    key: 'tarefa_humana',
    nodeType: 'tarefa_humana',
    label: 'Tarefa humana',
    description: 'Pausa o processo aguardando a decisão de uma pessoa (aprovação).',
    icon: UserCheck,
    grupo: 'Atividades',
    defaultConfig: {},
  },
  {
    key: 'subworkflow',
    nodeType: 'subworkflow',
    label: 'Subprocesso',
    description: 'Chama outro processo publicado como parte deste.',
    icon: WorkflowIcon,
    grupo: 'Atividades',
    defaultConfig: {},
  },
  {
    // v3.0.0: "Decisão" absorve a antiga "Decisão exclusiva" — os dois blocos
    // se comportavam igual no motor (segue a primeira saída verdadeira).
    key: 'condicao',
    nodeType: 'condicao',
    label: 'Decisão',
    description: 'Segue por UM caminho: o primeiro cuja regra for verdadeira (ou o caminho padrão).',
    icon: Filter,
    grupo: 'Decisões',
    defaultConfig: {},
  },
  {
    // v3.0.0: a antiga "Junção" virou um MODO deste bloco (`gateway_kind`),
    // escolhido no painel lateral: abrir caminhos x juntar caminhos.
    key: 'gateway_paralelo',
    nodeType: 'gateway',
    label: 'Execução paralela',
    description: 'Abre vários caminhos ao mesmo tempo — ou aguarda os caminhos abertos (modo juntar).',
    icon: Split,
    grupo: 'Decisões',
    defaultConfig: { gateway_kind: 'paralelo' },
  },

  {
    key: 'evento_intermediario',
    nodeType: 'espera',
    label: 'Evento intermediário',
    description: 'Aguarda um evento do sistema (ex.: "Proposta aceita") para seguir.',
    icon: MailQuestion,
    grupo: 'Eventos',
    defaultConfig: { wait_mode: 'evento' },
  },
  {
    key: 'exigencia',
    nodeType: 'espera',
    label: 'Exigência (pré-requisito)',
    description: 'Bloqueia ações do CRM até o pré-requisito ser cumprido (ex.: proposta aceita).',
    icon: ShieldAlert,
    grupo: 'Decisões',
    defaultConfig: {
      node_kind: 'exigencia',
      wait_mode: 'evento',
      enforcement: 'ENFORCE',
      guard_actions: [],
      event_codes: [],
    },

  },
  {
    key: 'espera',
    nodeType: 'espera',
    label: 'Temporizador',
    description: 'Aguarda uma duração ou uma data antes de continuar.',
    icon: Timer,
    grupo: 'Eventos',
    defaultConfig: { wait_mode: 'duracao', duration_minutes: 60 },
  },

  {
    key: 'fim',
    nodeType: 'fim',
    label: 'Fim',
    description: 'Encerra o ramo (e a instância quando não restam ramos).',
    icon: Flag,
    grupo: 'Encerramento',
    defaultConfig: {},
  },
];

/** Ordem dos grupos exibidos na paleta */
export const GRUPOS_DA_PALETA: Array<WorkflowPaletteItem['grupo']> = [
  'Atividades',
  'Decisões',
  'Eventos',
  'Encerramento',
];

/** Aparência por tipo de node (usada em listas e legendas fora do canvas) */
export const NODE_APPEARANCE: Record<WorkflowNodeType, { icon: LucideIcon; label: string; accent: string }> = {
  inicio: { icon: Play, label: 'Início', accent: 'text-primary' },
  acao: { icon: Zap, label: 'Tarefa automática', accent: 'text-primary' },
  condicao: { icon: Filter, label: 'Decisão', accent: 'text-warning' },
  gateway: { icon: Split, label: 'Execução paralela', accent: 'text-primary' },
  espera: { icon: Timer, label: 'Evento / espera', accent: 'text-muted-foreground' },
  tarefa_humana: { icon: UserCheck, label: 'Tarefa humana', accent: 'text-primary' },
  subworkflow: { icon: WorkflowIcon, label: 'Subprocesso', accent: 'text-primary' },
  fim: { icon: Flag, label: 'Fim', accent: 'text-destructive' },
};

/** Descreve em português o resumo de configuração exibido no card do node */
export function describeNodeConfig(nodeType: WorkflowNodeType, config: Record<string, any> | null): string | null {
  const cfg = config || {};

  if (nodeType === 'gateway') {
    const kind = String(cfg.gateway_kind || 'exclusivo');
    if (kind === 'paralelo') return 'Abre caminhos em paralelo';
    if (kind === 'juncao') {
      const policy = String(cfg.join_policy || 'all');
      if (policy === 'any') return 'Juntar caminhos: segue no primeiro ramo que chegar';
      if (policy === 'n_de_m') return `Juntar caminhos: aguarda ${cfg.join_count || '?'} ramos`;
      return 'Juntar caminhos: aguarda todos os ramos';
    }
    // v3.0.0: gateway exclusivo legado se comporta como Decisão
    return 'Segue por um único caminho';
  }

  if (nodeType === 'espera') {
    // v3.1.0: regra de liberação do bloco ('any' padrão | 'all' exige todos)
    const exigeTodos = String(cfg.event_match || 'any') === 'all';

    // Exigência: mostra o que está bloqueado enquanto o pré-requisito não vem
    if (String(cfg.node_kind || '') === 'exigencia') {
      // v1.1.0: a exigência aceita vários eventos de liberação (`event_codes`)
      const eventos: string[] = Array.isArray(cfg.event_codes)
        ? cfg.event_codes
        : cfg.event_code
          ? [String(cfg.event_code)]
          : [];
      const qtd = Array.isArray(cfg.guard_actions) ? cfg.guard_actions.length : 0;
      const alvo =
        eventos.length === 0
          ? 'evento não definido'
          : eventos.length === 1
            ? eventos[0]
            : `${eventos.length} acontecimentos${exigeTodos ? ' (todos)' : ''}`;
      return `Exige "${alvo}"${qtd > 0 ? ` — bloqueia ${qtd} ação(ões)` : ''}`;
    }

    const mode = String(cfg.wait_mode || 'evento');
    // v1.2.0: o bloco de espera também aceita lista de eventos (`event_codes`)
    const esperados: string[] = Array.isArray(cfg.event_codes)
      ? (cfg.event_codes as string[])
      : cfg.event_code
        ? [String(cfg.event_code)]
        : [];
    const alvo =
      esperados.length === 0
        ? 'não definido'
        : esperados.length === 1
          ? esperados[0]
          : `${esperados.length} eventos${exigeTodos ? ' (todos)' : ''}`;
    if (mode === 'duracao') return `Aguarda ${cfg.duration_minutes ?? 60} min`;
    if (mode === 'data') return `Aguarda até ${cfg.wake_at || 'data não definida'}`;
    if (mode === 'evento_ou_prazo') return `Aguarda "${alvo}" ou ${cfg.duration_minutes ?? 60} min`;
    return `Aguarda evento "${alvo}"`;

  }

  if (nodeType === 'tarefa_humana') {
    const kind = String(cfg.human_kind || 'aprovacao');
    return kind === 'aprovacao' ? 'Aprovação de pessoa' : 'Tarefa operacional';
  }

  return null;
}
